import { dateTimeNowForExcel } from "../../../utils/format";
import { INNER_BORDER_COLOR, OUTER_BORDER_COLOR } from "../../styles/colors";
import {
  COSTPER_NUMBER_FORMAT,
  PERCENT_NUMBER_FORMAT,
  TOTAL_NUMBER_FORMAT,
  allEdges,
  applyGridStyle,
  applyRangeFormats,
  quantityPlusUOMnumberFormat,
  rangeFormatting,
} from "../../styles/worksheet";
import { safeWorksheetName } from "../../workbook";
import { generateContractingSOV } from "../contractingSOV";
import { columnNames, rangeNames, rowNames } from "../helpers/rangeNames";
import { generateEstimateSOV } from "../sov";
import { titleMaker } from "./common";

type SOVtypes = "Estimate" | "Contracting";
export const createSOVTemplate = async (SOVtype: SOVtypes = "Estimate", includeUseGroups: boolean = false) => {
  try {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const isContracting = SOVtype === "Contracting";
      // Contracting always includes use groups; don't label it "Cross Tab"
      const showUseGroups = includeUseGroups || isContracting;
      // The Estimate Cross Tab's block is generic: its Cross Tab Columns option decides what fills it
      const columnBlockName = isContracting ? columnNames.useGroups : columnNames.crossTab;
      const sheetLabel = isContracting ? "Contracting SOV" : `${SOVtype} ${includeUseGroups ? "Cross Tab SOV" : "SOV"}`;
      const safeName = await safeWorksheetName(workbook, sheetLabel);
      const sheet = workbook.worksheets.add(safeName);
      await context.sync();

      await titleMaker(sheet, "Schedule of Values", isContracting ? "contracting" : "estimate");

      const rowHeights = [12, 30, 25, 25, 12, 25, 12, 25, 25, 12, 25];
      rowHeights.forEach((height, index) => {
        sheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
      });

      // Column layout varies by SOV type
      // Contracting: A(spacer) B(Code) C(Desc) D(Total) E($/GSF) F(spacer) G(Budget) H(Delta) I(spacer) J(Holds) K(Allowances) L(TradeCleanup) M(spacer) N-O(BuyStats) P(spacer) Q-R(Bids) S(spacer) T-U(UseGroups) V(spacer) W-X(ScopeAlternates)
      // Estimate+UG: A(spacer) B(Code) C(Desc) D(Total) E($/GSF) F(spacer) G-H(UseGroups)
      // Estimate:    A(spacer) B(Code) C(Desc) D(Total) E($/GSF)
      const columnWidths = isContracting
        ? [20, 50, 250, 85, 75, 12, 85, 85, 12, 85, 85, 85, 12, 95, 95, 12, 95, 95, 12, 95, 95, 12, 95, 95]
        : showUseGroups
          ? [20, 50, 250, 85, 75, 12, 95, 12, 12]
          : [20, 50, 400, 85, 75];
      columnWidths.forEach((width, index) => {
        sheet.getRange(`${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`).format.columnWidth =
          width;
      });

      sheet.getRange("1:15").format.verticalAlignment = Excel.VerticalAlignment.center;
      await context.sync();
      const itemsRowName = SOVtype === "Estimate" ? rowNames.estimateSOV : rowNames.contractingSOV;
      const markupRowName = SOVtype === "Estimate" ? rowNames.markups : rowNames.contractingMarkups;

      // Column position helpers — Contracting adds spacer + Budget+Delta+Holds+Allowances after $/GSF
      const costPerCol = "E";
      const outerCol = isContracting ? "M" : "F";
      const buyStatsStart = isContracting ? "N" : undefined;
      const buyStatsEnd = isContracting ? "O" : undefined;
      const buyStatsOuter = isContracting ? "P" : undefined;
      const bidsStart = isContracting ? "Q" : undefined;
      const bidsEnd = isContracting ? "R" : undefined;
      const bidsOuter = isContracting ? "S" : undefined;
      const ugStart = isContracting ? "T" : "G";
      const ugEnd = isContracting ? "U" : "H";
      const ugOuter = isContracting ? "V" : "I";
      const altStart = isContracting ? "W" : undefined;
      const altEnd = isContracting ? "X" : undefined;
      const altOuter = isContracting ? "Y" : undefined;

      // Create the table header
      const headers: rangeFormatting[] = [
        {
          rangeAddress: `${costPerCol}1`,
          value: dateTimeNowForExcel(),
          numberFormat: "mm/dd/yyyy",
          bold: true,
          name: rangeNames.date,
        },
        {
          rangeAddress: `${costPerCol}4`,
          value: "=GSF",
          name: rangeNames.gsf,
          Halignment: "Center",
          numberFormat: quantityPlusUOMnumberFormat("GSF"),
          bold: true,
        },
        { rangeAddress: "B6", value: "Code", name: "\\code", Halignment: "Center" },
        { rangeAddress: "B6", name: "\\markup_percent" },
        {
          rangeAddress: "C6",
          value: "Description",
          indentLevel: 1,
          name: isContracting ? "\\label" : "\\name",
          Halignment: "Left",
        },
        { rangeAddress: "D6", value: "Total", name: "\\total", Halignment: "Center" },
        { rangeAddress: `${costPerCol}6`, value: "$/GSF", name: `\\${columnNames.costPerGSF}`, Halignment: "Center" },
      ];
      if (isContracting) {
        headers.push(
          { rangeAddress: "G6", value: "Budget", name: `\\${columnNames.budget}`, Halignment: "Center" },
          { rangeAddress: "H6", value: "Delta", name: `\\${columnNames.budgetDelta}`, Halignment: "Center" },
          { rangeAddress: "J6", value: "Holds", name: `\\${columnNames.holds}`, Halignment: "Center" },
          { rangeAddress: "K6", value: "Allowances", name: `\\${columnNames.allowances}`, Halignment: "Center" },
          { rangeAddress: "L6", value: "Trade Cleanup", name: `\\${columnNames.tradeCleanup}`, Halignment: "Center" }
        );
      }
      if (buyStatsStart) {
        // buy stats named range — two cells wide initially, dynamically resized by handleBuyStatsColumns
        headers.push({
          rangeAddress: `${buyStatsStart}6:${buyStatsEnd}6`,
          name: `\\${columnNames.buyStats}`,
          Halignment: "Center",
        });
      }
      if (bidsStart) {
        // bids named range — two cells wide initially, dynamically resized by handleBidColumns
        headers.push({
          rangeAddress: `${bidsStart}6:${bidsEnd}6`,
          name: `\\${columnNames.bids}`,
          Halignment: "Center",
        });
      }
      if (showUseGroups) {
        // the usegroup named range cells must first be two cells wide for the borders to create correctly
        headers.push({
          rangeAddress: `${ugStart}6:${ugEnd}6`,
          name: `\\${columnBlockName}`,
          Halignment: "Center",
        });
        headers.push({ rangeAddress: `${ugStart}4`, name: "__costDenominators", italic: true });
      }
      if (altStart) {
        headers.push({
          rangeAddress: `${altStart}6:${altEnd}6`,
          name: `\\${columnNames.scopeAlternates}`,
          Halignment: "Center",
        });
      }
      headers.forEach((header) => {
        header.fontSize = 12;
        header.bold = true;
      });
      applyRangeFormats(sheet, headers);

      // Create the table rows
      const dataRows: rangeFormatting[] = [
        {
          // the items named range cells must first be two cells wide for the borders to create correctly
          rangeAddress: "B7:B8",
          name: itemsRowName,
          Halignment: "Center",
          numberFormat: "@",
        },
        { rangeAddress: "C7", Halignment: "Left", indentLevel: 1 },
        { rangeAddress: "D7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: `${costPerCol}7`, numberFormat: COSTPER_NUMBER_FORMAT, Halignment: "Right", italic: true },
        { rangeAddress: "C10", value: "Subtotal", indentLevel: 1, bold: true, Halignment: "Right" },
        {
          rangeAddress: "D10",
          numberFormat: TOTAL_NUMBER_FORMAT,
          Halignment: "Right",
          value: "=sum(D6:D9)",
          name: rowNames.subTotals,
          bold: true,
        },
        {
          rangeAddress: `${costPerCol}10`,
          numberFormat: COSTPER_NUMBER_FORMAT,
          Halignment: "Right",
          value: `=sum(${costPerCol}6:${costPerCol}9)`,
          bold: true,
          italic: true,
        },
        { rangeAddress: "B12", Halignment: "Center", numberFormat: PERCENT_NUMBER_FORMAT },
        {
          // the markup named range cells must first be two cells wide for the borders to create correctly
          rangeAddress: "C12:C13",
          name: markupRowName,
          indentLevel: 1,
          Halignment: "Left",
        },
        { rangeAddress: "D12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: `${costPerCol}12`, numberFormat: COSTPER_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "C15", value: "Total", indentLevel: 1, bold: true, Halignment: "Right" },
        {
          rangeAddress: "D15",
          numberFormat: TOTAL_NUMBER_FORMAT,
          Halignment: "Right",
          value: "=sum(D10:D14)",
          name: rowNames.grandTotal,
          bold: true,
        },
        {
          rangeAddress: `${costPerCol}15`,
          numberFormat: COSTPER_NUMBER_FORMAT,
          Halignment: "Right",
          value: `=sum(${costPerCol}10:${costPerCol}14)`,
          bold: true,
          italic: true,
        },
      ];
      if (isContracting) {
        // Budget, Delta, Holds, and Allowances data + subtotal + grand total formatting
        dataRows.push(
          { rangeAddress: "G7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "G10",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(G6:G9)",
            bold: true,
          },
          {
            rangeAddress: "G15",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(G10:G14)",
            bold: true,
          },
          { rangeAddress: "H7", numberFormat: "#,##0;(#,##0);-", Halignment: "Right" },
          {
            rangeAddress: "H10",
            numberFormat: "#,##0;(#,##0);-",
            Halignment: "Right",
            value: "=sum(H6:H9)",
            bold: true,
          },
          {
            rangeAddress: "H15",
            numberFormat: "#,##0;(#,##0);-",
            Halignment: "Right",
            value: "=sum(H10:H14)",
            bold: true,
          },
          { rangeAddress: "J7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "J10",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(J6:J9)",
            bold: true,
          },
          { rangeAddress: "J12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "J15",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(J10:J14)",
            bold: true,
          },
          { rangeAddress: "K7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "K10",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(K6:K9)",
            bold: true,
          },
          { rangeAddress: "K12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "K15",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(K10:K14)",
            bold: true,
          },
          { rangeAddress: "L7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "L10",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(L6:L9)",
            bold: true,
          },
          { rangeAddress: "L12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: "L15",
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: "=sum(L10:L14)",
            bold: true,
          }
        );
      }
      if (buyStatsStart) {
        dataRows.push(
          { rangeAddress: `${buyStatsStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${buyStatsStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${buyStatsStart}6:${buyStatsStart}9)`,
            bold: true,
          },
          { rangeAddress: `${buyStatsStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${buyStatsStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${buyStatsStart}10:${buyStatsStart}14)`,
            bold: true,
          }
        );
      }
      if (bidsStart) {
        dataRows.push(
          { rangeAddress: `${bidsStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${bidsStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${bidsStart}6:${bidsStart}9)`,
            bold: true,
          },
          { rangeAddress: `${bidsStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${bidsStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${bidsStart}10:${bidsStart}14)`,
            bold: true,
          }
        );
      }
      if (showUseGroups) {
        dataRows.push(
          { rangeAddress: `${ugStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${ugStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${ugStart}6:${ugStart}9)`,
            bold: true,
          },
          { rangeAddress: `${ugStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${ugStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${ugStart}10:${ugStart}14)`,
            bold: true,
          }
        );
      }
      if (altStart) {
        dataRows.push(
          { rangeAddress: `${altStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${altStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${altStart}6:${altStart}9)`,
            bold: true,
          },
          { rangeAddress: `${altStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${altStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${altStart}10:${altStart}14)`,
            bold: true,
          }
        );
        // ???: The H/J/K/L rows below are byte-identical to the ones already pushed by the
        // `if (isContracting)` block above — and altStart is only set when isContracting, so
        // every Contracting SOV template build applied these formats/formulas twice.
        // Commented out rather than deleted pending team confirmation that the duplication
        // wasn't intentional; delete once confirmed.
        // dataRows.push(
        //   { rangeAddress: "H7", numberFormat: "#,##0;(#,##0);-", Halignment: "Right" },
        //   {
        //     rangeAddress: "H10",
        //     numberFormat: "#,##0;(#,##0);-",
        //     Halignment: "Right",
        //     value: "=sum(H6:H9)",
        //     bold: true,
        //   },
        //   {
        //     rangeAddress: "H15",
        //     numberFormat: "#,##0;(#,##0);-",
        //     Halignment: "Right",
        //     value: "=sum(H10:H14)",
        //     bold: true,
        //   },
        //   { rangeAddress: "J7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "J10",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(J6:J9)",
        //     bold: true,
        //   },
        //   { rangeAddress: "J12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "J15",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(J10:J14)",
        //     bold: true,
        //   },
        //   { rangeAddress: "K7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "K10",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(K6:K9)",
        //     bold: true,
        //   },
        //   { rangeAddress: "K12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "K15",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(K10:K14)",
        //     bold: true,
        //   },
        //   { rangeAddress: "L7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "L10",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(L6:L9)",
        //     bold: true,
        //   },
        //   { rangeAddress: "L12", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
        //   {
        //     rangeAddress: "L15",
        //     numberFormat: TOTAL_NUMBER_FORMAT,
        //     Halignment: "Right",
        //     value: "=sum(L10:L14)",
        //     bold: true,
        //   }
        // );
      }
      if (buyStatsStart) {
        dataRows.push(
          { rangeAddress: `${buyStatsStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${buyStatsStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${buyStatsStart}6:${buyStatsStart}9)`,
            bold: true,
          },
          { rangeAddress: `${buyStatsStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${buyStatsStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${buyStatsStart}10:${buyStatsStart}14)`,
            bold: true,
          }
        );
      }
      if (bidsStart) {
        dataRows.push(
          { rangeAddress: `${bidsStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${bidsStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${bidsStart}6:${bidsStart}9)`,
            bold: true,
          },
          { rangeAddress: `${bidsStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${bidsStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${bidsStart}10:${bidsStart}14)`,
            bold: true,
          }
        );
      }
      if (showUseGroups) {
        dataRows.push(
          { rangeAddress: `${ugStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${ugStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${ugStart}6:${ugStart}9)`,
            bold: true,
          },
          { rangeAddress: `${ugStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${ugStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${ugStart}10:${ugStart}14)`,
            bold: true,
          }
        );
      }
      if (altStart) {
        dataRows.push(
          { rangeAddress: `${altStart}7`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${altStart}10`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${altStart}6:${altStart}9)`,
            bold: true,
          },
          { rangeAddress: `${altStart}12`, numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
          {
            rangeAddress: `${altStart}15`,
            numberFormat: TOTAL_NUMBER_FORMAT,
            Halignment: "Right",
            value: `=sum(${altStart}10:${altStart}14)`,
            bold: true,
          }
        );
      }
      applyRangeFormats(sheet, dataRows);

      // Grid styles — parameterized by column positions
      // For Contracting, split grid ranges around spacer columns F and I.
      // Left block: B–E (Code through $/GSF), Right block 1: G–H (Budget, Delta), Right block 2: J–K (Holds, Allowances)
      // For Estimate, single block B–E as before.
      const leftDataCol = "E";
      const rightBlock1Start = isContracting ? "G" : undefined;
      const rightBlock1End = isContracting ? "H" : undefined;
      const rightBlock2Start = isContracting ? "J" : undefined;
      const rightBlock2End = isContracting ? "L" : undefined;

      // sov items, must be two rows for the borders to create correctly
      applyGridStyle({
        range: sheet.getRange(`B7:${leftDataCol}8`),
        borderColor: INNER_BORDER_COLOR,
        weight: "Thin",
        edges: allEdges,
      });
      applyGridStyle({
        range: sheet.getRange("A7"),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeRight"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}7:${rightBlock1End}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: allEdges,
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}7:${rightBlock2End}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: allEdges,
        });
      }
      applyGridStyle({
        range: sheet.getRange(`${outerCol}7`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeLeft"],
      });
      // spacer row
      applyGridStyle({
        range: sheet.getRange(`B9:${leftDataCol}9`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeTop"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}9:${rightBlock1End}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}9:${rightBlock2End}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      // subtotal
      applyGridStyle({
        range: sheet.getRange(`D10:${leftDataCol}10`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeLeft", "EdgeRight"],
      });
      applyGridStyle({
        range: sheet.getRange(`D9:${leftDataCol}9`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeBottom"],
      });
      applyGridStyle({
        range: sheet.getRange(`D11:${leftDataCol}11`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeTop"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}10:${rightBlock1End}10`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}9:${rightBlock1End}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}11:${rightBlock1End}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}10:${rightBlock2End}10`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}9:${rightBlock2End}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}11:${rightBlock2End}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      // markups, must be two rows for the borders to create correctly
      applyGridStyle({
        range: sheet.getRange(`B12:${leftDataCol}13`),
        borderColor: INNER_BORDER_COLOR,
        weight: "Thin",
        edges: allEdges,
      });
      applyGridStyle({
        range: sheet.getRange("A12"),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeRight"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}12:${rightBlock1End}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: allEdges,
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}12:${rightBlock2End}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: allEdges,
        });
      }
      applyGridStyle({
        range: sheet.getRange(`${outerCol}12`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeLeft"],
      });
      // top of markups, must be applied after the markups gridlines
      applyGridStyle({
        range: sheet.getRange("B11:C11"),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeBottom"],
      });
      applyGridStyle({
        range: sheet.getRange(`D11:${leftDataCol}11`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeBottom", "EdgeTop"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}11:${rightBlock1End}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}11:${rightBlock2End}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
      }
      // bottom of markups
      applyGridStyle({
        range: sheet.getRange(`B14:${leftDataCol}14`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeTop"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}14:${rightBlock1End}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}14:${rightBlock2End}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      // grand total
      applyGridStyle({
        range: sheet.getRange(`D15:${leftDataCol}15`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeLeft", "EdgeRight"],
      });
      applyGridStyle({
        range: sheet.getRange(`D14:${leftDataCol}14`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeBottom"],
      });
      applyGridStyle({
        range: sheet.getRange(`D16:${leftDataCol}16`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: ["EdgeTop"],
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}15:${rightBlock1End}15`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}14:${rightBlock1End}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}16:${rightBlock1End}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}15:${rightBlock2End}15`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}14:${rightBlock2End}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}16:${rightBlock2End}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }

      if (bidsStart) {
        // sov items — thin borders inside (between rows and between bid columns)
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}7:${bidsEnd}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // spacer row
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}9:${bidsEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // subtotal
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}9:${bidsEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}11:${bidsEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // markups — thin borders inside
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}12:${bidsEnd}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // top of markups
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}11:${bidsEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
        // bottom of markups
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}14:${bidsEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // grand total
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}14:${bidsEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${bidsStart}16:${bidsEnd}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }

      if (buyStatsStart) {
        // sov items — thin borders inside
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}7:${buyStatsEnd}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // spacer row
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}9:${buyStatsEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // subtotal
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}9:${buyStatsEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}11:${buyStatsEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // markups — thin borders inside
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}12:${buyStatsEnd}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // top of markups
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}11:${buyStatsEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
        // bottom of markups
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}14:${buyStatsEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // grand total
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}14:${buyStatsEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${buyStatsStart}16:${buyStatsEnd}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }

      // Spacer columns — outer borders at header(6), data(7), subtotal(10), markups(12), grandtotal(15)
      if (isContracting) {
        const spacerCols = ["F", "I", outerCol, buyStatsOuter!, bidsOuter!];
        const spacerRows = [6, 7, 10, 12, 15];
        for (const col of spacerCols) {
          for (const row of spacerRows) {
            applyGridStyle({
              range: sheet.getRange(`${col}${row}`),
              borderColor: OUTER_BORDER_COLOR,
              weight: "Medium",
              edges: ["EdgeLeft", "EdgeRight"],
            });
          }
        }
      }

      if (showUseGroups) {
        // sov items
        applyGridStyle({
          range: sheet.getRange(`${ugStart}7:${ugEnd}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}7`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugEnd}7`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugOuter}7`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        // spacer row
        applyGridStyle({
          range: sheet.getRange(`${ugStart}9:${ugEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // subtotal
        applyGridStyle({
          range: sheet.getRange(`${ugStart}10:${ugEnd}10`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugOuter}10`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}9:${ugEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}11:${ugEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // markups, must be two rows for the borders to create correctly
        applyGridStyle({
          range: sheet.getRange(`${ugStart}12:${ugEnd}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}12`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugEnd}12`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugOuter}12`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        // top of markups
        applyGridStyle({
          range: sheet.getRange(`${ugStart}11:${ugEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
        // bottom of markups
        applyGridStyle({
          range: sheet.getRange(`${ugStart}14:${ugEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // grand total
        applyGridStyle({
          range: sheet.getRange(`${ugStart}15:${ugEnd}15`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft", "EdgeRight"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugOuter}15`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeLeft"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}14:${ugEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${ugStart}16:${ugEnd}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
      }

      if (altStart) {
        // sov items — thin borders inside
        applyGridStyle({
          range: sheet.getRange(`${altStart}7:${altEnd}8`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // spacer row
        applyGridStyle({
          range: sheet.getRange(`${altStart}9:${altEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // subtotal
        applyGridStyle({
          range: sheet.getRange(`${altStart}9:${altEnd}9`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${altStart}11:${altEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // markups — thin borders inside
        applyGridStyle({
          range: sheet.getRange(`${altStart}12:${altEnd}13`),
          borderColor: INNER_BORDER_COLOR,
          weight: "Thin",
          edges: ["InsideHorizontal", "InsideVertical"],
        });
        // top of markups
        applyGridStyle({
          range: sheet.getRange(`${altStart}11:${altEnd}11`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom", "EdgeTop"],
        });
        // bottom of markups
        applyGridStyle({
          range: sheet.getRange(`${altStart}14:${altEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });
        // grand total
        applyGridStyle({
          range: sheet.getRange(`${altStart}14:${altEnd}14`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeBottom"],
        });
        applyGridStyle({
          range: sheet.getRange(`${altStart}16:${altEnd}16`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: ["EdgeTop"],
        });

        // Thick borders on spacer between use groups and alternates (ugOuter)
        // and on the column after alternates (altOuter)
        const borderRows = [6, 7, 10, 12, 15];
        for (const row of borderRows) {
          applyGridStyle({
            range: sheet.getRange(`${ugOuter}${row}`),
            borderColor: OUTER_BORDER_COLOR,
            weight: "Medium",
            edges: ["EdgeLeft", "EdgeRight"],
          });
          applyGridStyle({
            range: sheet.getRange(`${altOuter}${row}`),
            borderColor: OUTER_BORDER_COLOR,
            weight: "Medium",
            edges: ["EdgeLeft"],
          });
        }
      }

      await context.sync();

      // Generate SOV report
      if (SOVtype === "Contracting") {
        await generateContractingSOV(sheet);
      } else {
        await generateEstimateSOV(sheet);
      }

      // and then the header
      applyGridStyle({
        range: sheet.getRange(`B6:${leftDataCol}6`),
        borderColor: OUTER_BORDER_COLOR,
        weight: "Medium",
        edges: allEdges,
      });
      if (rightBlock1Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock1Start}6:${rightBlock1End}6`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }
      if (rightBlock2Start) {
        applyGridStyle({
          range: sheet.getRange(`${rightBlock2Start}6:${rightBlock2End}6`),
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }
      if (bidsStart) {
        const bidsHeaderRange = sheet.getRange(`\\${columnNames.bids}`);
        applyGridStyle({
          range: bidsHeaderRange,
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }
      if (buyStatsStart) {
        const buyStatsHeaderRange = sheet.getRange(`\\${columnNames.buyStats}`);
        applyGridStyle({
          range: buyStatsHeaderRange,
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }
      if (showUseGroups) {
        const useGroupHeaderRange = sheet.getRange(`\\${columnBlockName}`);
        applyGridStyle({
          range: useGroupHeaderRange,
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }
      if (altStart) {
        const altHeaderRange = sheet.getRange(`\\${columnNames.scopeAlternates}`);
        applyGridStyle({
          range: altHeaderRange,
          borderColor: OUTER_BORDER_COLOR,
          weight: "Medium",
          edges: allEdges,
        });
      }

      sheet.showGridlines = false;
      sheet.activate();
      await context.sync();
    });
  } catch (error) {
    throw "Error creating " + SOVtype + " SOV template: " + error;
  }
};
